import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button, { RemoveButton } from '@/components/Button';
import { FormLayout, Input, Label, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import {
  EditorialFacebookPostResource,
  EditorialQueuePage,
} from '@/types/resources';
import { api } from '@/utils/api';
import {
  EditorialFacebookPostNetwork,
  EditorialFacebookPostType,
} from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';
import { RootState } from '@/utils/store';

import PostFormOptionsField from './PostFormOptionsField';

// import PostTypeField from './PostTypeField';

const NetworkInput = styled(Input)`
  text-transform: capitalize;
`;

const VariationWrap = styled(Larger)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-row-gap: 24px;
  grid-column-gap: 24px;
  margin: 0 -32px;
  padding: 0 32px;
  padding: 24px 32px;
  background-color: ${({ theme }) => theme.colors.grayscaleSuperLight};
`;

const VariationHeading = styled(Larger)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NumberBadge = styled(Text)`
  display: flex;
  height: 24px;
  width: 48px;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.colors.purpleLighter};
  border-radius: 100px;
`;

const AddButtonHolder = styled.div`
  text-align: right;
  margin-bottom: 8px;
  svg {
    margin-right: 8px;
  }
  button {
    width: 100%;
  }
`;

export interface FormDataT {
  post_type: 'article' | 'video';
  message: string;
  video: string;
  comment: string;
  image_url: string;
  image: string;
  link: string;
  owner: string;
  site_visit_benchmark: string;
  reach_duration: number;
  option: string;
  additional_channels_ids?: { channel_id: number | null }[];
}

interface Props {
  onClose: VoidFunction;
  activePage: EditorialQueuePage;
  post?: EditorialFacebookPostResource;
}

const PostForm: FC<Props> = ({ onClose, activePage, post }) => {
  const alerts = useAlerts();

  const { network } = useParams();

  const isNetworkIg =
    post?.network === EditorialFacebookPostNetwork.INSTAGRAM ||
    network === 'instagram';

  const queryClient = useQueryClient();

  const userName = useSelector((state: RootState) => state.auth.user!.name);

  const { data: channels } = useQuery(
    ['editorial_distribution_queue_channels'],
    () => api.get<never, EditorialQueuePage[]>('/v1/editorial/queue/channels'),
    {
      staleTime: 120_000,
      select: (data) =>
        data.map((channel) => ({ label: channel.name, value: channel.id })),
    },
  );

  const {
    handleSubmit,
    clearErrors,
    control,
    watch,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      post_type:
        post?.type === EditorialFacebookPostType.VIDEO ? 'video' : 'article',
      message: post?.message ?? '',
      video: '',
      image_url: post?.imageUrl ?? '',
      comment: '',
      link: post?.link ?? '',
      image: '',
      owner: post?.owner ?? userName,
      reach_duration: 60,
      site_visit_benchmark: post?.siteVisitBenchmark?.toString() ?? '0',
      option: 'prepend',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'additional_channels_ids',
  });

  const selectedChannelIds =
    watch('additional_channels_ids')?.map((item) => item.channel_id) || [];

  const filteredChannels = channels?.filter(
    (channel) =>
      channel.value !== activePage.id &&
      !selectedChannelIds.includes(channel.value),
  );

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    return handleSubmit(async ({ additional_channels_ids, ...data }) => {
      try {
        post
          ? await api.put(`/v1/editorial/facebook-posts/${post.id}`, data)
          : await api.post(`/v1/editorial/queue/prepend`, {
              ...data,
              network: isNetworkIg
                ? EditorialFacebookPostNetwork.INSTAGRAM
                : EditorialFacebookPostNetwork.FACEBOOK,
              channel_id: activePage.id,
              ...(!!additional_channels_ids?.length && {
                additional_channels_ids,
              }),
            });

        await queryClient.invalidateQueries([
          'editorial_distribution_schedules',
        ]);

        onClose();

        alerts.success('A new post has been added successfully');
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  const type = watch('post_type');

  return (
    <Modal>
      <SidePanel
        heading={post ? 'Edit post' : `Add new post`}
        onClose={onClose}
      >
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              {/* <PostTypeField control={control} disabled={!!post} /> */}
              <Larger>
                <Label>Network</Label>
                <NetworkInput readOnly value={network} />
              </Larger>
              {type === 'video' && !post && (
                <FormField<FormDataT>
                  id="video"
                  label="Video"
                  control={control}
                  name="video"
                  type="video"
                />
              )}
              {!post && <PostFormOptionsField control={control} />}
              <FormField<FormDataT>
                label="Message"
                type="textarea"
                control={control}
                name="message"
              />
              {type === 'article' && (
                <>
                  {!isNetworkIg && (
                    <FormField<FormDataT>
                      label="Comment"
                      type="textarea"
                      control={control}
                      placeholder="Add comment. Eg.: Read more - <article link>"
                      name="comment"
                    />
                  )}
                  <FormField<FormDataT>
                    label="Article Link"
                    control={control}
                    name="link"
                    inputType="url"
                  />
                  {!isNetworkIg ? (
                    <FormField<FormDataT>
                      label="Image URL"
                      control={control}
                      placeholder="Add URL"
                      name="image_url"
                      inputType="url"
                    />
                  ) : (
                    !post && (
                      <FormField<FormDataT>
                        id="igImage"
                        label="Image"
                        control={control}
                        name="image"
                        type="image"
                        notes="JPG up to 8MB. Aspect ratio 1:1"
                        accept="image/jpeg"
                      />
                    )
                  )}
                </>
              )}
              <FormField<FormDataT>
                label="Owner"
                control={control}
                name="owner"
              />
              <Larger>
                <Label>Page Name</Label>
                <NetworkInput readOnly value={activePage.name} />
              </Larger>
              {!post && (
                <>
                  {fields.map((item, index) => (
                    <VariationWrap key={item.id}>
                      <VariationHeading>
                        <NumberBadge $type="smallBold" $color="white">
                          Nr. {index + 2}
                        </NumberBadge>
                        <RemoveButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(index);
                          }}
                        >
                          Remove
                        </RemoveButton>
                      </VariationHeading>
                      <FormField<FormDataT, number>
                        label="Page Name"
                        type="select"
                        control={control}
                        name={`additional_channels_ids.${index}.channel_id`}
                        options={filteredChannels || []}
                      />
                    </VariationWrap>
                  ))}
                  <Larger>
                    <AddButtonHolder>
                      <Button
                        type="button"
                        $type="colorized"
                        onClick={() => append({ channel_id: null })}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8 2V14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M2 8H14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Add additional page
                      </Button>
                    </AddButtonHolder>
                  </Larger>
                </>
              )}
              {type === 'article' && (
                <>
                  <FormField<FormDataT>
                    label="Site Visit Benchmark"
                    control={control}
                    placeholder="Add site visit benchmark"
                    name={`site_visit_benchmark`}
                  />
                  <FormField<FormDataT>
                    label="Reach Duration (min)"
                    control={control}
                    placeholder="Add reach duration"
                    disabled
                    name={`reach_duration`}
                  />
                </>
              )}
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              {post ? 'Update' : 'Add'}
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default PostForm;
